import { IBusinessEntitySummaryPage } from '@commenda/types';
import { SWRFetcher } from '@commenda/utils';
import { useMemo } from 'react';
import useSWR from 'swr';

/** One lightweight row in the global corporations list. */
export interface BusinessEntityCorporationRow {
  corporationId: number;
  name: string | null;
}

export interface UseBusinessEntityCorporationsParams {
  search?: string;
  cursor?: number;
  limit?: number;
  /** Restrict to sales-tax-enabled corporations (salesTaxCorporationId set). */
  salesTaxEnabled?: boolean;
}

function buildCorporationsQuery(params: UseBusinessEntityCorporationsParams): string {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.cursor) query.append('cursor', params.cursor.toString());
  if (params.limit) query.append('limit', params.limit.toString());
  if (params.salesTaxEnabled) query.append('salesTaxEnabled', 'true');
  return query.toString();
}

/** Lists controlled corporations across every company (cursor-paginated, name search). */
export function useBusinessEntityCorporations(params: UseBusinessEntityCorporationsParams) {
  const query = buildCorporationsQuery(params);
  const key = `business-entities/summaries${query ? `?${query}` : ''}`;

  const { data, error, isLoading, mutate } = useSWR<IBusinessEntitySummaryPage>(
    key,
    SWRFetcher({ method: 'GET' }),
    { keepPreviousData: true }
  );

  const corporations = useMemo<BusinessEntityCorporationRow[]>(
    () => (data?.entities ?? []).map((entity) => ({ corporationId: entity.id, name: entity.legalName })),
    [data]
  );

  return {
    corporations,
    nextCursor: data?.nextCursor ?? null,
    isLoading,
    error,
    mutate,
  };
}
